import type { TranslationKey } from './i18n';

/**
 * The chat's emoji, by category, drawn by the computer's own emoji font. Kept to
 * Emoji 12 and older, without skin tones, joined sequences or flags, so a
 * Windows 10 machine draws every one (Windows draws no flags at all).
 */
export interface EmojiCategory {
  id: string;
  label: TranslationKey;
  /** The category's tab. */
  icon: string;
  emoji: string[];
}

const list = (text: string) => text.trim().split(/\s+/);

export const EMOJI_CATEGORIES: EmojiCategory[] = [
  {
    id: 'smileys',
    label: 'chat.emoji.smileys',
    icon: '😀',
    emoji: list(`
      😀 😃 😄 😁 😆 😅 🤣 😂 🙂 🙃 😉 😊 😇 🥰 😍 🤩 😘 😗 😚 😙 😋 😛 😜 🤪 😝 🤑 🤗 🤭 🤫 🤔
      🤐 🤨 😐 😑 😶 😏 😒 🙄 😬 🤥 😌 😔 😪 🤤 😴 😷 🤒 🤕 🤢 🤮 🤧 🥵 🥶 🥴 😵 🤯 🤠 🥳 😎 🤓
      🧐 😕 😟 🙁 😮 😯 😲 😳 🥺 😦 😧 😨 😰 😥 😢 😭 😱 😖 😣 😞 😓 😩 😫 🥱 😤 😡 😠 🤬 😈 👿
      💀 💩 🤡 👻 👽 🤖 😺 😸 😹 😻 😼 😽 🙀 😿 😾 🙈 🙉 🙊
      👋 🤚 ✋ 🖖 👌 🤏 🤞 🤟 🤘 🤙 👈 👉 👆 👇 👍 👎 ✊ 👊 🤛 🤜 👏 🙌 👐 🤲 🤝 🙏 💅 💪
      👀 👂 👃 🧠 👶 🧒 👦 👧 🧑 👨 👩 🧓 👴 👵 🙋 🙇 🤦 🤷 💁 🙅 🙆
    `),
  },
  {
    id: 'nature',
    label: 'chat.emoji.nature',
    icon: '🐱',
    emoji: list(`
      🐶 🐱 🐭 🐹 🐰 🦊 🐻 🐼 🐨 🐯 🦁 🐮 🐷 🐸 🐵 🐔 🐧 🐦 🐤 🦆 🦅 🦉 🦇 🐺 🐗 🐴 🦄 🐝 🐛 🦋
      🐌 🐞 🐜 🦗 🦂 🐢 🐍 🦎 🐙 🦑 🦐 🦀 🐡 🐠 🐟 🐬 🐳 🐋 🦈 🐊 🐅 🐆 🦓 🦍 🐘 🦛 🦏 🐪 🐫 🦒
      🦘 🐃 🐂 🐄 🐎 🐏 🐑 🦙 🐐 🦌 🐕 🐩 🐈 🐓 🦃 🦚 🦜 🦢 🐇 🦝 🦦 🦥 🐁 🦔 🐾
      🌵 🎄 🌲 🌳 🌴 🌱 🌿 🍀 🎍 🍃 🍂 🍁 🍄 🌾 💐 🌷 🌹 🥀 🌺 🌸 🌼 🌻
      🌞 🌝 🌛 🌜 🌚 🌕 🌙 🌎 🌍 🌏 🪐 💫 ⭐ 🌟 ✨ ⚡ 💥 🔥 🌈 ⛅ ⛄ 💨 💧 💦 ☔ 🌊
    `),
  },
  {
    id: 'food',
    label: 'chat.emoji.food',
    icon: '🍔',
    emoji: list(`
      🍏 🍎 🍐 🍊 🍋 🍌 🍉 🍇 🍓 🍈 🍒 🍑 🥭 🍍 🥥 🥝 🍅 🍆 🥑 🥦 🥬 🥒 🌽 🥕 🧄 🧅 🥔 🍠
      🥐 🥯 🍞 🥖 🥨 🧀 🥚 🍳 🧈 🥞 🧇 🥩 🍗 🍖 🌭 🍔 🍟 🍕 🥪 🥙 🧆 🌮 🌯 🥗 🥘 🥫 🍝 🍜
      🍲 🍛 🍣 🍱 🥟 🍤 🍙 🍚 🍘 🍥 🥠 🥮 🍢 🍡 🍧 🍨 🍦 🥧 🧁 🍰 🎂 🍮 🍭 🍬 🍫 🍿 🍩 🍪
      🌰 🥜 🍯 🥛 🍼 ☕ 🍵 🧃 🥤 🧉 🧊 🥄 🍴 🥣 🥡 🥢 🧂
    `),
  },
  {
    id: 'activities',
    label: 'chat.emoji.activities',
    icon: '⚽',
    emoji: list(`
      ⚽ 🏀 🏈 ⚾ 🥎 🎾 🏐 🏉 🥏 🎱 🏓 🏸 🏒 🏑 🥍 🏏 🥅 ⛳ 🏹 🎣 🤿 🥊 🥋 🎽 🛹 🛷 🥌 🎿
      🏆 🥇 🥈 🥉 🏅 🎫 🎪 🎭 🎨 🎬 🎤 🎧 🎼 🎹 🥁 🎷 🎺 🎸 🪕 🎻 🎲 🎯 🎳 🎮 🧩
      🎉 🎊 🎈 🎁 🎀 🎃 🎆 🎇 🧨 🏃 🚶 🧘 🚴 🏊
    `),
  },
  {
    id: 'travel',
    label: 'chat.emoji.travel',
    icon: '🚗',
    emoji: list(`
      🚗 🚕 🚙 🚌 🚎 🚓 🚑 🚒 🚐 🚚 🚛 🚜 🛵 🚲 🛴 🚨 🚔 🚍 🚘 🚖 🚡 🚠 🚃 🚋 🚝 🚄 🚅 🚈
      🚂 🚆 🚇 🚊 🚉 🛫 🛬 💺 🚀 🛸 🚁 🛶 ⛵ 🚤 🚢 ⚓ ⛽ 🚧 🚦 🚥 🗿 🗽 🗼 🏰 🏯 🎡 🎢 🎠
      ⛲ 🌋 🗻 ⛺ 🏠 🏡 🏭 🏢 🏬 🏣 🏤 🏥 🏦 🏨 🏪 🏫 🏩 💒 ⛪ 🕌 🕍 🕋 🌅 🌄 🌠 🌇 🌆 🌃 🌌 🌉 🌁
    `),
  },
  {
    id: 'objects',
    label: 'chat.emoji.objects',
    icon: '💡',
    emoji: list(`
      ⌚ 📱 📲 💻 💽 💾 💿 📀 📷 📸 📹 🎥 📞 📟 📠 📺 📻 ⏰ ⌛ ⏳ 📡 🔋 🔌 💡 🔦 🧯
      💸 💵 💴 💶 💷 💰 💳 💎 🧰 🔧 🔨 🔩 🧱 🧲 🔮 🧿 📿 💈 🔭 🔬 💊 💉 🩹 🩺 🧬 🦠 🧪
      🧹 🧺 🧻 🚿 🛁 🧼 🧽 🔑 🚪 🧸 🛒 ✉️ 📩 📨 📧 💌 📥 📤 📦 📪 📫 📬 📭 📮 📯 📜 📃 📄 📑
      🧾 📊 📈 📉 📆 📅 📇 📋 📁 📂 📰 📓 📔 📒 📕 📗 📘 📙 📚 📖 🔖 🧷 🔗 📎 📐 📏 🧮 📌 📍
      ✂️ ✒️ 📝 ✏️ 🔍 🔎 🔏 🔐 🔒 🔓
    `),
  },
  {
    id: 'symbols',
    label: 'chat.emoji.symbols',
    icon: '❤️',
    emoji: list(`
      ❤️ 🧡 💛 💚 💙 💜 🖤 🤍 🤎 💔 ❣️ 💕 💞 💓 💗 💖 💘 💝 💟 💯 💢 💬 💭 💤
      ✅ ☑️ ✔️ ❌ ❎ ➕ ➖ ➗ ✖️ ❓ ❔ ❗ ❕ ‼️ ⁉️ ⚠️ 🚫 ⛔ 🛑 📛 🔰 ♻️ ⭕ 🆗 🆕 🆓 🆒 🆙 🆘 🆔
      🔴 🟠 🟡 🟢 🔵 🟣 🟤 ⚫ ⚪ 🟥 🟧 🟨 🟩 🟦 🟪 🟫 ⬛ ⬜ 🔶 🔷 🔸 🔹 🔺 🔻 💠 🔘
      ➡️ ⬅️ ⬆️ ⬇️ ↗️ ↘️ ↙️ ↖️ ↕️ ↔️ ↩️ ↪️ 🔄 🔃 🔀 🔁 🔂 ▶️ ⏸️ ⏹️ ⏩ ⏪ 🎵 🎶 🔔 🔕 📣 📢 ♾️ ™️ ©️ ®️
    `),
  },
];

const RECENT_KEY = 'viganium.chat.recentEmoji';
const RECENT_MAX = 24;

/** The emoji this browser used last, newest first — kept per browser, like the theme. */
export function recentEmoji(): string[] {
  try {
    const saved = JSON.parse(localStorage.getItem(RECENT_KEY) ?? '[]');
    return Array.isArray(saved) ? saved.filter((e): e is string => typeof e === 'string').slice(0, RECENT_MAX) : [];
  } catch {
    return [];
  }
}

export function rememberEmoji(emoji: string): void {
  try {
    const next = [emoji, ...recentEmoji().filter((e) => e !== emoji)].slice(0, RECENT_MAX);
    localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  } catch {
    /* a private window may keep nothing; the picker works without it */
  }
}

/** One to three emoji and nothing else — a message the chat shows large, as WhatsApp does. */
export function onlyEmoji(text: string): boolean {
  const trimmed = text.trim();
  if (!trimmed || trimmed.length > 24) return false;
  const marks = trimmed.replace(/[\s\uFE0F\u200D]/g, '');
  const pictographs = marks.match(/\p{Extended_Pictographic}/gu) ?? [];
  if (pictographs.length === 0 || pictographs.length > 3) return false;
  // Only pictographs (and the marks that dress them) — no letters, digits or punctuation.
  return marks.replace(/\p{Extended_Pictographic}|\p{Emoji_Modifier}|[\u20E3]/gu, '') === '';
}
